import {FC, useState} from 'react';
import {connect, useSelector} from 'react-redux';

import logger from '~src/services/logger/LlsLogger';
import { Uuid } from '~src/services/uuid';
import { maxLength, validatorMaxLength80, validatorRequired } from '~src/services/validators';
import { SectionOpt } from '~src/shared/form/translatableSectionForm/interfaces';
import { translateContent } from '~src/services/translationServices';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { getFormattedSubjectAndUseCaseTranslationKey } from '~src/modules/surveys/services/survey';
import { deleteSurvey, editSurvey } from '~src/modules/surveys/actions/surveyActions';
import TranslatableSectionForm from '~src/shared/form/translatableSectionForm/TranslatableSectionForm';
import ItemDetailsHeader from '~src/shared/headers/itemDetalsHeader/ItemDetailsHeader';
import {
  getDefaultSurveyInvitation,
  Survey,
  surveyMaxLengthInvitation,
  SurveyScopes,
} from '~src/modules/surveys/interfaces';
import {
  ApiReduxResp,
  getLoggingContext,
  isSuccess,
  ThunkMWDispatch,
} from '~src/services/apiMiddleware';
import { StoreI } from '~src/rootReducer';
import { MenuItemConfI } from '~src/shared/headers/itemDetalsHeader/types';
import { ContentLocalesI, SystemLocales } from '~src/interfaces/session.types';
import { routes } from '~src/routingConfig';
import { ShowNotification } from '~src/store/app/interfaces';
import { showNotification } from '~src/store/app/actions';
import {useSessionUser} from "~src/services/hooks/useAuthenticatedUser";
import {useTranslation} from "react-i18next";
import {useContentLocale} from "~src/services/hooks/useContentLocale";
import {useHistory} from "react-router";

interface OwnProps {
  survey: Survey;
  isEditFormInitiallyVisible?: boolean;
}

interface DispatchProps {
  showNotification: ShowNotification;
  deleteSurvey(id: string): ApiReduxResp<void>;
  editSurvey(id: string, formData: Record<string, unknown>): ApiReduxResp<unknown>;
}

type Props = OwnProps & DispatchProps;

export const SurveyHeader: FC<Props> = (props: Props) => {
  const [isEditFormVisible, setIsEditFormVisible] = useState(props.isEditFormInitiallyVisible);
  const [deletionError, setDeletionError] = useState<string|null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadingEdit = useSelector((store: StoreI) => store.surveyManagement.surveys.loadingEdit);
  const loadingDel = useSelector((store: StoreI) => store.surveyManagement.surveys.loadingDel);
  const me = useSessionUser();
  const userLocale = useContentLocale();
  const history = useHistory();

  const { t } = useTranslation();

  const goToReport = () => {
    history.push(routes.reports.survey(Uuid.fromHexString(props.survey.id)));
  };


  const duplicateSurvey = () => {
    const route = routes.contentSurveys.surveys;
    if (props.survey.scope !== SurveyScopes.Admin && me) {
      history.push(routes.reports.getUserProfileMyRequestsLink(me.uuid), {
        copySurveyId: props.survey.id,
      });
    } else {
      history.push(routes.contentSurveys.surveys, { copySurveyId: props.survey.id });
    }
    history.push(route, { copySurveyId: props.survey.id });
  };

  const menuItems: MenuItemConfI[] = [
      {
        iconName: 'iconReports',
        txtKey: `x.survey.detail.header.report_action${
          props.survey.scope !== SurveyScopes.Admin ? '.request' : ''
        }`,
        onClick: goToReport,
      },
      {
        iconName: 'iconPlusBox',
        txtKey: `x.survey.detail.header.copy_action${
          props.survey.scope !== SurveyScopes.Admin ? '.request' : ''
        }`,
        onClick: duplicateSurvey,
      },
    ];


  const onEditSubmit = (formData: Record<string, unknown>) =>
    props.editSurvey(props.survey.id, formData).then((response) => {
      if (isSuccess(response)) {
        toggleEditFrm();
      } else {
        logger.info('Error editing Survey', getLoggingContext(response, 'SurveyHeader'));
      }
      return response;
    });

  const deleteSurvey = (): ApiReduxResp<void> => {
    setIsDeleting(true);
    return props.deleteSurvey(props.survey.id).then((response) => {
      if (isSuccess(response)) {
        // Due to the time has taken on the server to manage deletion and update the survey's list
        setTimeout(() => {
          goToListAfterDeletion();
          const txtKey =
            props.survey.scope === SurveyScopes.Admin
              ? 'x.surveys.survey_deleted_message'
              : 'x.surveys.request_deleted_message';
          const surveyName = translateContent(props.survey.name);
          props.showNotification({
            id: `${txtKey}.${surveyName}`,
            txtKey,
            txtProps: { name: surveyName },
          });
        }, 5000);
      } else {
        let deletionError = 'x.surveys.schedules.delete.error.unknownError';
        logger.info('Error deleting Survey', getLoggingContext(response, 'SurveyHeader'));
        if (response.error.message.indexOf('404') !== -1) {
          deletionError = 'x.surveys.delete.error.not_found';
        }
        setDeletionError(deletionError);
        setIsDeleting(false);
      }
      return response;
    });
  };

  const toggleEditFrm = () => setIsEditFormVisible(!isEditFormVisible);

  const goToListAfterDeletion = () => {
    let route = routes.contentSurveys.surveys;
    if (props.survey.scope !== SurveyScopes.Admin && me) {
      route = `/reports/user/profile/${me.uuid}/list/my-requests`;
    }
    history.push(route);
  };

  const goToList = () => history.goBack();

  const getSubTitle = () => {
    if (props.survey.scope === SurveyScopes.Admin) {
      return {
        key: `x.survey.detail.header.survey_type, ${getFormattedSubjectAndUseCaseTranslationKey(props.survey)}`,
        txt: `${t('x.survey.detail.header.survey_type')} ${t(
          getFormattedSubjectAndUseCaseTranslationKey(props.survey),
        )}`,
      };
    } else {
      return {
        key: 'x.survey.detail.header.request',
        txt: t('x.survey.detail.header.request'),
      };
    }
  };

  const { survey, } = props;

  const textSizeValidator = maxLength(surveyMaxLengthInvitation);

  const defaultSurveyInvitationTexts: ContentLocalesI = getDefaultSurveyInvitation(t);
  // If provided locale don't have a value, we want to fallback to this locale
  // fallbackLocale is used only to 'show' a value, when the form is saved, it is actually
  // saved with `locale` (https://loopline-systems.atlassian.net/browse/LN-485?focusedCommentId=29204)
  const fallbackLocale = SystemLocales.EnUs;
  const editSurveySections: SectionOpt[] = [
    {
      name: 'info',
      fields: [
        {
          name: 'name',
          label: 'x.shared.name_and_description_form.input.placeholder.name',
          validator: composeValidators([validatorRequired, validatorMaxLength80]),
          value: { ...survey.name, [userLocale]: survey.name[userLocale] ?? survey.name[fallbackLocale] },
          translationValidator: validatorMaxLength80,
        },
        {
          name: 'invitation',
          label: 'x.shared.name_and_description_form.input.placeholder.invitation',
          validator: textSizeValidator,
          value: {
            ...defaultSurveyInvitationTexts,
            ...survey.invitation,
            [userLocale]:
              survey.invitation && (survey.invitation[userLocale] ?? survey.invitation[fallbackLocale]),
          },
          translationValidator: textSizeValidator,
          type: 'textArea',
        },
      ],
    },
  ];

  return (
    <ItemDetailsHeader
      t={t}
      isEditFrmVisible={isEditFormVisible}
      deleteItem={deleteSurvey}
      loadingDel={loadingDel || isDeleting}
      onBackClick={goToList}
      toggleEditFrm={toggleEditFrm}
      backArrow={{ key: 'x.survey.detail.header.back' }}
      supTitle={getSubTitle()}
      title={{ key: 'be', txt: translateContent(survey.name) }}
      description={survey.invitation && { key: 'be', txt: translateContent(survey.invitation) }}
      menuEdit={{ key: 'x.survey.detail.edit' }}
      menuDel={{
        key: `x.survey.detail.delete${props.survey.scope !== SurveyScopes.Admin ? '.request' : ''}`,
      }}
      additionalMenuItems={menuItems}
      deletionTitleKey={`x.surveys.delete_confirmation.headline${
        props.survey.scope !== SurveyScopes.Admin ? '.user' : ''
      }`}
      deletionContentKey={`x.surveys.delete_confirmation.content${
        props.survey.scope !== SurveyScopes.Admin ? '.user' : ''
      }`}
      deletionError={deletionError}
    >
      <TranslatableSectionForm
        t={t}
        titleKey="be"
        title={translateContent(survey.name)}
        form={`editSurvey_${survey.id}`}
        sections={editSurveySections}
        onSubmit={onEditSubmit}
        onCancel={toggleEditFrm}
        loadingSave={loadingEdit}
        inContainer
      />
    </ItemDetailsHeader>
  );
}

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  deleteSurvey: id => dispatch(deleteSurvey(id)),
  editSurvey: (id, formData) => dispatch(editSurvey(id, formData)),
  showNotification: notification => dispatch(showNotification(notification)),
});

// @ts-expect-error ThunkMWDispatch is different from what React expects
const SurveyHeaderC = connect(undefined, mapDispatchToProps)(SurveyHeader);

export default SurveyHeaderC;
